import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BPSAdminLevel } from 'src/app/models/user.model';
import { MeterRepayment } from 'src/app/models/meterRepayment.model';
import { MultiSelectObject } from 'src/app/models/util.model';
import { MeterRepaymentService } from 'src/app/services/meter-repayment.service';

@Component({
  selector: 'crud-meter-repayment',
  templateUrl: './crud-meter-repayment.component.html',
  styleUrls: ['./crud-meter-repayment.component.scss'],
})
export class CrudMeterRepaymentComponent implements OnInit {
  @Input() searchText: string = '';
  @Input() utilityId: number = 0;
  @Input() bpsAdminLevel!: BPSAdminLevel;

  @ViewChild('closeAddEditModal') closeAddEditModal!: ElementRef;
  @ViewChild('closeDeleteModal') closeDeleteModal!: ElementRef;
  @ViewChild('openEditModalBtn') openEditModalBtn!: ElementRef;

  readonly vendorNamesMaxLength = 60;

  currentPage = 1;
  pageSize = 10;
  count = 0;
  clicked: boolean = false;
  isEditMode = false;

  meterRepayments: MeterRepayment[] = [];
  selectedItem: MeterRepayment = new MeterRepayment();
  vendorItems: MultiSelectObject[] = [];
  vendorError: string = '';

  constructor(
    private toastr: ToastrService,
    private meterRepaymentService: MeterRepaymentService
  ) {}

  ngOnInit(): void {
    this.reloadTable();
  }

  reloadTable(page: number | null = null): void {
    if (page) this.currentPage = page;
    const dataIn = {
      pageInfo: { page: this.currentPage, pageSize: this.pageSize },
      utilityId: this.utilityId,
    };
    this.meterRepaymentService.getAll(dataIn).subscribe({
      next: (res) => {
        const response = res?.body;
        if (response?.status === 200 || response?.status === 'OK') {
          this.meterRepayments = response.data.data;
          this.count = response.data.count;
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  filter(): void {
    this.reloadTable(1);
  }

  pageChange(value: number): void {
    this.currentPage = value;
    this.reloadTable();
  }

  addNew(): void {
    this.isEditMode = false;
    this.selectedItem = new MeterRepayment();
    this.vendorError = '';
    this.getVendors(0, []);
    this.clicked = !this.clicked;
  }

  openEditModal(item: MeterRepayment): void {
    this.selectItem(item);
    this.openEditModalBtn.nativeElement.click();
  }

  selectItem(item: MeterRepayment): void {
    this.isEditMode = true;
    this.selectedItem = { ...item };
    this.vendorError = '';
    this.getVendors(
      item.id,
      (item.vendors ?? []).map((x) => x.id)
    );
    this.clicked = !this.clicked;
  }

  onModalFade(): void {
    this.selectedItem = new MeterRepayment();
    this.vendorItems = this.vendorItems.map((x) => ({ ...x, checked: false }));
    this.vendorError = '';
  }

  onVendorChange(event: any): void {
    const item = this.vendorItems.find((x) => x.id === event.id);
    if (item) item.checked = event.checked;
    if (this.vendorItems.some((x) => x.checked)) this.vendorError = '';
  }

  getVendorNames(item: MeterRepayment): string {
    return (item.vendors ?? []).map((x) => x.name).join(', ');
  }

  getVendorNamesShort(item: MeterRepayment): string {
    const names = this.getVendorNames(item);
    return names.length > this.vendorNamesMaxLength
      ? names.slice(0, this.vendorNamesMaxLength).trimEnd() + '...'
      : names;
  }

  onRepaymentPeriodKeydown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (allowed.includes(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  customMessage(event: any): void {
    event.target.setCustomValidity('Please enter the number of repayment months as a positive whole number.');
  }

  clearCustomMessage(event: any): void {
    event.target.setCustomValidity('');
  }

  save(): void {
    if (!this.vendorItems.some((x) => x.checked)) {
      this.vendorError = 'COMMON.METER_REPAYMENT.VENDOR_REQUIRED';
      return;
    }

    const dataIn = {
      id: this.selectedItem.id,
      utilityId: this.utilityId,
      repaymentPeriod: this.selectedItem.repaymentPeriod,
      vendorIds: this.vendorItems.filter((x) => x.checked).map((x) => x.id),
    };

    this.meterRepaymentService.save(dataIn).subscribe({
      next: (res) => {
        const response = res?.body;
        if (response?.status === 200 || response?.status === 'OK') {
          this.closeAddEditModal?.nativeElement?.click();
          this.toastr.success(response.message);
          this.reloadTable(this.isEditMode ? null : 1);
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  deleteItem(): void {
    this.meterRepaymentService.delete(this.selectedItem.id).subscribe({
      next: (res) => {
        const response = res?.body;
        this.closeDeleteModal?.nativeElement?.click();
        if (response?.status === 200 || response?.status === 'OK') {
          this.toastr.success(response.message);
          this.reloadTable(1);
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }

  private getVendors(meterRepaymentId: number, selectedVendorIds: number[]): void {
    this.meterRepaymentService.getVendorsForSelect(meterRepaymentId, this.utilityId).subscribe({
      next: (res) => {
        const response = res?.body;
        if (response?.status === 200 || response?.status === 'OK') {
          this.vendorItems = (response.data ?? []).map((o: any) => ({
            id: o.id,
            name: o.name,
            checked: selectedVendorIds.includes(o.id),
          }));
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: () => this.toastr.error('An error occurred.'),
    });
  }
}
